import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { empresaService } from '@/services/api';
import { Empresa, EmpresaFilters } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

export const EmpresasPage: React.FC = () => {
  const [filters, setFilters] = useState<EmpresaFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEmpresas } = useQueryInvalidation();

  const { data: empresasData, isLoading, error } = useQuery({
    queryKey: ['empresas', filters],
    queryFn: () => empresaService.list(filters),
  });

  const deleteEmpresaMutation = useMutation({
    mutationFn: empresaService.delete,
    onSuccess: () => {
      invalidateEmpresas();
      handleSuccess('Empresa excluída com sucesso!');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao excluir empresa');
    },
  });

  // Busca via debounce já ocorre no useEffect acima

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Empresa',
      'Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.',
      async () => {
        await deleteEmpresaMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar empresas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie as empresas cadastradas no sistema
          </p>
        </div>
        <Link to="/empresas/nova">
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            Nova Empresa
          </Button>
        </Link>
      </div>

      {/* Busca */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Filtros</CardTitle>
              <CardDescription>Busque e filtre as empresas cadastradas</CardDescription>
            </div>
            <div className="w-full md:w-72 relative">
              <input
                type="text"
                placeholder="Buscar por nome, fantasia ou CNPJ"
                className="w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Empresas List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Empresas</CardTitle>
          <CardDescription>
            {empresasData?.pagination.total || 0} empresas encontradas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : empresasData?.data.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma empresa encontrada</p>
              <Link to="/empresas/nova">
                <Button className="mt-4">Cadastrar primeira empresa</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {empresasData?.data.map((empresa: Empresa) => (
                <div
                  key={empresa.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{empresa.nome}</h3>
                      <p className="text-sm text-gray-600">{empresa.nomeFantasia}</p>
                      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {empresa.cnpj && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">CNPJ:</span> {empresa.cnpj}
                          </p>
                        )}
                        {empresa.email && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Email:</span> {empresa.email}
                          </p>
                        )}
                        {empresa.telefone1 && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Telefone:</span> {empresa.telefone1}
                          </p>
                        )}
                        {empresa.cidade && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Cidade:</span> {empresa.cidade.nome}/{empresa.cidade.uf}
                          </p>
                        )}
                        {empresa.representante && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Representante:</span> {empresa.representante}
                          </p>
                        )}
                        {empresa.cargoRepresentante && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Cargo:</span> {empresa.cargoRepresentante}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Link to={`/empresas/${empresa.id}/editar`}>
                        <Button variant="outline" size="sm">
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDelete(empresa.id)}
                        disabled={deleteEmpresaMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {empresasData && empresasData.pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={!empresasData.pagination.hasPrev}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-sm text-gray-700">
                  Página {empresasData.pagination.page} de {empresasData.pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={!empresasData.pagination.hasNext}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteEmpresaMutation.isPending}
      />
    </div>
  );
};
